import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef
} from 'react';
import Details from './details.svg';
import * as styles from './RateTable.module.scss';
import Badge from '@components/Badge';
import useMatchMedia from '@hooks/useMatchMedia';
import useConfig from '@hooks/useConfig';
import classNames from 'classnames';
import { formatCurrency, formatPercent } from '@helpers';
import { MAX_LOADING_ROWS } from '@constants';
import { columnKeys } from '@enums';

const mobileTableWidth = parseInt(styles.mobileTableWidth);

export default React.memo(function RateTable({
  rates = [],
  isLoading: _isLoading,
  hasInitialData,
  footerIsVisible,
  setModalProduct
}) {
  const isMobile = useMatchMedia(mobileTableWidth);
  const [key, setKey] = useState('ssr');

  useEffect(() => {
    setKey('rendered');
  }, []);

  let rows = useMemo(() => {
    return (
      rates?.map((data, index) => (
        <Row
          key={`${key}-${index}`}
          isMobile={isMobile}
          setModalProduct={setModalProduct}
          {...data}
        />
      )) ?? null
    );
  }, [rates, key, isMobile, setModalProduct]);

  const isLoading = _isLoading && (!hasInitialData || !rows?.length);

  if (isLoading && footerIsVisible && rows?.length > MAX_LOADING_ROWS) {
    rows = rows.slice(0, MAX_LOADING_ROWS);
  }

  return (
    <div id="calculator-rate-table" className={styles.tableWrapper}>
      <div
        className={styles.table}
        aria-live="polite"
        aria-label={isLoading ? 'Loading rates' : null}
      >
        {!isLoading || rows?.length ? (
          rows
        ) : (
          <PlaceholderRows isMobile={isMobile} />
        )}
      </div>
    </div>
  );
});

const Row = React.memo(({ isMobile, setModalProduct, ...data }) => {
  const config = useConfig();

  const rowProps = useMemo(() => {
    const { closingCosts } = data;
    let pointsComponent = null;

    // Map badges to individual columns
    const columnBadges = {
      [columnKeys.PRODUCT]: null,
      [columnKeys.RATE]: null,
      [columnKeys.POINTS]: null,
      [columnKeys.PAYMENT]: null
    };

    if (Array.isArray(data.badges)) {
      data.badges.forEach(key => {
        const badge = config.badges[key];

        if (badge && badge.badgeDisplayColumn in columnBadges) {
          columnBadges[badge.badgeDisplayColumn] = badge;
        }
      });
    }

    // Format closing costs value
    if (data.isNCC || closingCosts === 0) {
      pointsComponent = '$0.00';
    } else {
      const isPositive = closingCosts > 0;
      const sign = isPositive ? '+' : '-';
      const points = formatCurrency(Math.abs(closingCosts));

      pointsComponent = (
        <>
          <span
            className={classNames(styles.sign, {
              [styles.negative]: !isPositive
            })}
          >
            {sign}
          </span>
          {points}
        </>
      );
    }

    return {
      columnBadges,
      payment: formatCurrency(data.payment),
      points: pointsComponent,
      apr: (
        <>
          {formatPercent(data.apr, true, false)}
          <span className={styles.percent}>%</span>
        </>
      ),
      rate: (
        <>
          {formatPercent(data.rate, true, false)}
          <span className={styles.percent}>%</span>
        </>
      )
    };
  }, [data, config.badges]);

  const openModal = useCallback(() => {
    setModalProduct(data);
  }, [data, setModalProduct]);

  const openModalRef = useRef();
  openModalRef.current = () => openModal();

  const handleClick = useCallback(() => openModalRef.current(), []);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      openModalRef.current();
    }
  }, []);

  const props = {
    ...rowProps,
    data,
    isMobile,
    openModal,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    enableDebugMode: config.enableDebugMode,
    ariaProps: {
      tabIndex: 0,
      role: 'button',
      'aria-controls': 'calculator-rate-modal'
    }
  };

  return (
    <div className="calculator-rate">
      <DesktopRow {...props} />
      <MobileRow {...props} />
    </div>
  );
});

function DesktopRow({
  data = {},
  apr,
  rate,
  payment,
  points,
  columnBadges = {},
  enableDebugMode,
  ariaProps = {},
  isPlaceholder,
  isMobile,
  onClick,
  onKeyDown
}) {
  return (
    <div
      className={classNames(styles.rowWrapper, {
        [styles.placeholder]: isPlaceholder
      })}
      onClick={isPlaceholder || isMobile ? null : onClick}
      onKeyDown={isPlaceholder || isMobile ? null : onKeyDown}
      aria-hidden={isPlaceholder || isMobile}
      {...ariaProps}
    >
      <div className={styles.row}>
        <Cell
          label="Mortgage type"
          badge={columnBadges[columnKeys.PRODUCT]}
          data-column={columnKeys.PRODUCT}
          isPlaceholder={isPlaceholder}
        >
          {data.productLabel}
        </Cell>
        <Cell
          label={
            <>
              Interest rate <LabelSlash /> APR
            </>
          }
          badge={columnBadges[columnKeys.RATE]}
          data-column={columnKeys.RATE}
          isPlaceholder={isPlaceholder}
        >
          {rate} <span className={styles.slash} /> {apr}
        </Cell>
        <Cell
          label="Points"
          badge={columnBadges[columnKeys.POINTS]}
          data-column={columnKeys.POINTS}
          isPlaceholder={isPlaceholder}
        >
          {points}
        </Cell>
        <Cell
          label="Estimated payment"
          badge={columnBadges[columnKeys.PAYMENT]}
          data-column={columnKeys.PAYMENT}
          isPlaceholder={isPlaceholder}
        >
          {payment}
          <span className={styles.month}>
            <span className="sr-only">per </span>month
          </span>
        </Cell>
      </div>
      {enableDebugMode && !isPlaceholder && (
        <DebugInfo className={styles.row} data={data} />
      )}
    </div>
  );
}

function LabelSlash() {
  return (
    <span className={styles.labelSlash}>
      <span className="sr-only">/</span>
    </span>
  );
}

function Cell({
  className,
  label,
  badge,
  children,
  isPlaceholder,
  showHighlightLabel,
  highlightLabel,
  ...restProps
}) {
  const labelText = showHighlightLabel ? (highlightLabel ?? label) : label;

  return (
    <div className={classNames(styles.cell, className)} {...restProps}>
      {(isPlaceholder || labelText || badge) && (
        <Label
          className={classNames({
            [styles.highlight]: showHighlightLabel && !isPlaceholder && !badge
          })}
        >
          {!badge?.badgeText ? (
            labelText
          ) : (
            <Badge
              className={styles.badge}
              badgeName={badge.badgeName}
              badgeStyle={badge.badgeStyle}
              tooltip={badge.tooltipText}
            >
              {badge.badgeText}
            </Badge>
          )}
        </Label>
      )}
      {(children || isPlaceholder) && <Value>{children}</Value>}
    </div>
  );
}

function MobileRow({
  data = {},
  apr,
  rate,
  payment,
  points,
  columnBadges = {},
  isPlaceholder,
  isMobile,
  enableDebugMode,
  ariaProps = {},
  onClick,
  onKeyDown
}) {
  return (
    <div className={styles.cardWrapper}>
      <div
        className={classNames(styles.card, {
          [styles.placeholder]: isPlaceholder
        })}
        onClick={isPlaceholder || !isMobile ? null : onClick}
        onKeyDown={isPlaceholder || !isMobile ? null : onKeyDown}
        aria-hidden={isPlaceholder || !isMobile}
        {...ariaProps}
      >
        <div className={styles.cardContent}>
          <CardData
            isPlaceholder={isPlaceholder}
            badge={columnBadges[columnKeys.PRODUCT]}
            label="Mortgage type"
            value={data.productLabel}
            valueClassName={styles.product}
            labelClassName={styles.productLabel}
            labelPlaceholderWidth={95}
            valuePlaceholderWidth={164}
            valuePlaceholderHeight={24}
          />
          <CardData
            isPlaceholder={isPlaceholder}
            className={styles.cardRates}
            labelClassName={styles.rateLabel}
            badge={columnBadges[columnKeys.RATE]}
            label={
              <>
                Interest rate <LabelSlash /> APR
              </>
            }
            value={
              <>
                {rate} <span className={styles.slash} /> {apr}
              </>
            }
            labelPlaceholderWidth={124}
            valuePlaceholderWidth={159}
            animationDelay={700}
          />
        </div>

        <div className={classNames(styles.cardContent, styles.cardBottom)}>
          <div className={styles.cardDetails}>
            <CardData
              isPlaceholder={isPlaceholder}
              badge={columnBadges[columnKeys.PAYMENT]}
              label="Estimated payment"
              value={
                <>
                  {payment}
                  <span className={styles.month}>
                    <span className="sr-only">per </span>month
                  </span>
                </>
              }
              labelPlaceholderWidth={113}
              valuePlaceholderWidth={122}
            />
            <CardData
              isPlaceholder={isPlaceholder}
              badge={columnBadges[columnKeys.POINTS]}
              label="Points"
              value={points}
              labelPlaceholderWidth={41}
              valuePlaceholderWidth={70}
              animationDelay={250}
            />
          </div>

          <div role="presentation" className={styles.detailsIcon}>
            <Details />
          </div>
        </div>
        {enableDebugMode && !isPlaceholder && <DebugInfo data={data} />}
      </div>
    </div>
  );
}

function CardData({
  className,
  label,
  badge,
  labelClassName,
  labelPlaceholderWidth = null,
  labelPlaceholderHeight = null,
  value,
  valueClassName,
  valuePlaceholderWidth = null,
  valuePlaceholderHeight = null,
  animationDelay,
  isPlaceholder,
  children,
  ...rest
}) {
  const hasBadge = !!badge?.badgeText;

  const labelComponent = hasBadge ? (
    // Don't show badge tooltips in the rate table on mobile
    <Badge
      className={classNames(styles.label, styles.mobileBadge, labelClassName)}
      badgeName={badge.badgeName}
      badgeStyle={badge.badgeStyle}
    >
      {badge.badgeText}
    </Badge>
  ) : (
    <Label className={labelClassName}>{label}</Label>
  );

  const placeholderLabelStyles = {
    width: hasBadge ? undefined : labelPlaceholderWidth,
    height: hasBadge ? undefined : labelPlaceholderHeight,
    animationDelay: !animationDelay ? null : `${animationDelay}ms`
  };

  return (
    <div className={classNames(styles.cardData, className)} {...rest}>
      {!isPlaceholder ? (
        <>
          {(hasBadge || label) && labelComponent}
          {value && <Value className={valueClassName}>{value}</Value>}
          {children}
        </>
      ) : (
        <>
          <Label style={placeholderLabelStyles}>
            {hasBadge && labelComponent}
          </Label>
          <Value
            className={valueClassName}
            style={{
              width: valuePlaceholderWidth,
              height: valuePlaceholderHeight,
              animationDelay: !animationDelay ? null : `${animationDelay}ms`
            }}
          />
        </>
      )}
    </div>
  );
}

function Label({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.label, className)} {...rest}>
      <div className={styles.cellContent}>{children}</div>
    </div>
  );
}

function Value({ className, children, ...rest }) {
  return (
    <div className={classNames(styles.value, className)} {...rest}>
      <strong className={styles.cellContent}>{children}</strong>
    </div>
  );
}

// Empty row used to display placeholder
// content while loading if there was
// no rate data previously in the table
function PlaceholderRows({ count = 4 }) {
  return Array.from({ length: count }, (_, index) => {
    return (
      <div key={index} className="calculator-rate">
        <DesktopRow isPlaceholder />
        <MobileRow isPlaceholder />
      </div>
    );
  });
}

function DebugInfo({ data, className }) {
  return (
    <div className={classNames(className, styles.debugInfo)}>
      <div className={styles.debugContent}>
        <div className={styles.debugItem}>
          <strong>Plan Number:</strong> {data.planNumber}
        </div>
        <div className={styles.debugItem}>
          <strong>Description:</strong> {data.planDescription}
        </div>
      </div>
    </div>
  );
}
